import { countWorkingDays } from './workingDays.js'

const toUtcDate = value => {
  if (typeof value !== 'string') return null
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(value)
  if (!match) return null
  return new Date(Date.UTC(Number(match[1]), Number(match[2]) - 1, Number(match[3])))
}

const getOverlap = (...ranges) => {
  const starts = []
  const ends = []
  for (let i = 0; i < ranges.length; i += 2) {
    if (ranges[i]) starts.push(toUtcDate(ranges[i]))
    if (ranges[i + 1]) ends.push(toUtcDate(ranges[i + 1]))
  }
  if (starts.some(date => !date) || ends.some(date => !date)) return null

  const start = new Date(Math.max(...starts.map(date => date.getTime())))
  const end = new Date(Math.min(...ends.map(date => date.getTime())))
  if (start > end) return null
  return { start, end }
}

/**
 * Hours that a recurring days-per-week allocation contributes to a week.
 * Partial periods use the five-day workweek: dpw × working days / 5 × 8.
 */
export function allocationHoursForWeek(
  allocation,
  weekStart,
  weekEnd,
  contractStart = null,
  contractEnd = null
) {
  if (!allocation?.start_date || !allocation?.end_date || !allocation?.dpw) return 0
  const overlap = getOverlap(
    allocation.start_date,
    allocation.end_date,
    weekStart,
    weekEnd,
    contractStart,
    contractEnd
  )
  if (!overlap) return 0

  const workingDays = countWorkingDays(
    overlap.start.toISOString().slice(0, 10),
    overlap.end.toISOString().slice(0, 10)
  )
  return (Number(allocation.dpw) * workingDays * 8) / 5
}

/**
 * Hours that a single localProposal contributes to a given week. Mirrors the
 * backend's per-week proportional calculation in routes/hours.js so the
 * client-side simulation lines up with what the server computes once the
 * proposal is persisted.
 */
export function localProposalHoursForWeek(lp, weekStart, weekEnd, contractStart, contractEnd) {
  if (!lp?.periodStart || !lp?.periodEnd || !lp?.dpw) return 0
  return allocationHoursForWeek(
    {
      start_date: lp.periodStart,
      end_date: lp.periodEnd,
      dpw: lp.dpw
    },
    weekStart,
    weekEnd,
    contractStart,
    contractEnd
  )
}

/**
 * Index a localProposals list by employeeId, optionally skipping entries on
 * a given preference (used when computing availability for that same
 * preference — its own staged proposal must not penalize the candidate).
 */
export function indexLocalProposalsByEmployee(localProposals, excludePreferenceId = null) {
  const map = new Map()
  for (const lp of localProposals || []) {
    if (excludePreferenceId != null && lp.preferenceId === excludePreferenceId) continue
    if (!map.has(lp.employeeId)) map.set(lp.employeeId, [])
    map.get(lp.employeeId).push(lp)
  }
  return map
}

/**
 * Total local-proposal hours for an employee in a given week, summed across
 * all their proposals on OTHER preferences in this prospect.
 */
export function localOtherHoursForWeek(empLocalProposals, weekStart, weekEnd) {
  return (empLocalProposals || []).reduce(
    (sum, lp) => sum + localProposalHoursForWeek(lp, weekStart, weekEnd),
    0
  )
}

/**
 * Compute the available_days a week effectively has after subtracting the
 * employee's local-staged proposals on OTHER preferences. Used by the
 * voorgesteld-mode availability simulation.
 *
 * `week.available_days` is assumed to already be the server-side
 * voorgesteld-mode value (i.e. proposed_hours from OTHER prospects already
 * deducted). We only further subtract the not-yet-persisted local proposals.
 */
export function adjustedAvailableDaysForWeek(week, empLocalOtherProposals) {
  const localOtherHours = localOtherHoursForWeek(
    empLocalOtherProposals,
    week.week_start,
    week.week_end
  )
  const baseAvailable = Number(week.available_days || 0)
  return Math.max(0, baseAvailable - localOtherHours / 8)
}
